import { useEffect, useMemo, useRef, useState } from 'react';
import { uniqBy } from 'lodash';
import { getWorkspaceId, useAppSelector } from '@zonofi/zono-money-store';
import { useDebouncedOfflineSearch } from './use-debounced-offline-search';
import { FilterConfig, useOfflineFilter } from './use-offline-filter';

interface CacheData<TItem> {
  data: TItem[];
  pageNo: number;
  completed: boolean;
}

const loadAllApiDataCache = new Map<string, CacheData<unknown>>();

function getCache<TItem>(key: string): CacheData<TItem> | undefined {
  return loadAllApiDataCache.get(key) as CacheData<TItem> | undefined;
}

function setCache<TItem>(key: string, value: CacheData<TItem>) {
  loadAllApiDataCache.set(key, value as CacheData<unknown>);
}

// ─── Normalized pagination shape the hook works with internally ───
interface NormalizedPagination {
  hasNextPage: boolean;
}

// ─── What the caller must provide to extract their API's shape ────
interface ApiResponseTransformer<TResponse, TItem> {
  getData: (response: TResponse) => TItem[];
  getPagination: (response: TResponse) => NormalizedPagination;
}

// ─── RTK Query state wrapper ──────────────────────────────────────
interface RtkQueryState<TResponse> {
  data?: TResponse;
  isLoading: boolean;
  isFetching?: boolean;
  isError: boolean;
  requestId?: string;
  reset?: () => void;
}

interface UseLoadAllApiDataProps<TItem, TResponse> {
  fetchDataApiCall: (params: { pageNo: number; maxPageSize: number }) => void;
  apiResponse: RtkQueryState<TResponse>;
  transformer: ApiResponseTransformer<TResponse, TItem>;
  uniqueKey: keyof TItem;
  cacheKey?: string;
  maxPageSize?: number;
  dependencies?: unknown[];
  searchText?: string;
  searchPaths?: string[];
  filters?: FilterConfig<TItem>[];
}

export const useLoadAllApiDataV2 = <TItem, TResponse>({
  fetchDataApiCall,
  apiResponse,
  transformer,
  uniqueKey,
  cacheKey,
  maxPageSize = 100,
  dependencies = [],
  searchText = '',
  searchPaths,
  filters = [],
}: UseLoadAllApiDataProps<TItem, TResponse>) => {
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [data, setData] = useState<TItem[]>([]);
  const [results, setResults] = useState<TItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchText);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [activeFilters, setActiveFilters] =
    useState<FilterConfig<TItem>[]>(filters);

  // Tracks which requestId we dispatched and whether it was consumed
  const pendingRequestId = useRef<string | null>(null);
  const pageNoRef = useRef(1);

  // Keep transformer ref stable so the response effect never needs it as a dep
  const transformerRef = useRef(transformer);
  useEffect(() => {
    transformerRef.current = transformer;
  });

  const { getSearchResults } = useDebouncedOfflineSearch<TItem>({
    searchPaths: searchPaths ?? [],
  });
  const { getFilteredResults } = useOfflineFilter<TItem>();

  const stableCacheKey = useMemo(
    () => (cacheKey ? `${cacheKey}-${sellerWorkspaceId}}` : undefined),
    [cacheKey, sellerWorkspaceId]
  );

  // ─── Reset + kick off page 1 whenever key deps change ────────────
  useEffect(() => {
    if (stableCacheKey) {
      const cached = getCache<TItem>(stableCacheKey);
      if (cached && stableCacheKey.includes(sellerWorkspaceId)) {
        setData(cached.data); // ✅ typed as TItem[]
        setCompleted(cached.completed);
        pageNoRef.current = cached.pageNo;
        pendingRequestId.current = null;

        if (!cached.completed) {
          fetchDataApiCall({
            pageNo: cached.pageNo + 1,
            maxPageSize,
          });
        }

        return;
      }
    }
    pageNoRef.current = 1;
    pendingRequestId.current = null;

    setData([]);
    setCompleted(false);
    setIsLoading(true);
    setIsPageLoading(false);

    apiResponse?.reset?.();
    fetchDataApiCall({ pageNo: 1, maxPageSize });
  }, [sellerWorkspaceId, refreshNonce, stableCacheKey, ...dependencies]);

  // ─── Capture the latest requestId as soon as RTK emits one ───────
  useEffect(() => {
    if (apiResponse.requestId) {
      pendingRequestId.current = apiResponse.requestId;
    }
  }, [apiResponse.requestId]);

  // ─── Process each settled response ───────────────────────────────
  // RTK Query: queries expose `isFetching`, mutations only expose `isLoading`.
  // Treat either being true as "in-flight"; settled = both false.
  const isInFlight = !!(apiResponse.isFetching || apiResponse.isLoading);

  useEffect(() => {
    // Still in-flight or nothing returned yet
    if (isInFlight) return;
    if (!apiResponse.data && !apiResponse.isError) return;

    if (apiResponse.isError) {
      setIsLoading(false);
      setIsPageLoading(false);
      setCompleted(true);
      return;
    }

    if (!apiResponse.data) return;

    const pageData = transformerRef.current.getData(apiResponse.data);
    const { hasNextPage } = transformerRef.current.getPagination(
      apiResponse.data
    );
    const currentPage = pageNoRef.current;

    setData((prev) => {
      const source = currentPage === 1 ? pageData : [...prev, ...pageData];

      return uniqBy(source, (item) => String(item[uniqueKey]));
    });

    if (hasNextPage && pageData.length > 0) {
      const nextPage = currentPage + 1;
      pageNoRef.current = nextPage;
      pendingRequestId.current = null;

      setIsLoading(false); // page-1 spinner off after first page lands
      setIsPageLoading(true);

      fetchDataApiCall({ pageNo: nextPage, maxPageSize });
    } else {
      setIsLoading(false);
      setIsPageLoading(false);
      setCompleted(true);
    }
  }, [isInFlight]);

  // ─── Debounce search input ────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // ─── Sync filter prop changes ─────────────────────────────────────
  useEffect(() => {
    setActiveFilters(filters);
  }, [JSON.stringify(filters)]);

  // ─── Apply search + filters to raw data ──────────────────────────
  useEffect(() => {
    if (!data.length) {
      setResults([]);
      return;
    }
    const searched = getSearchResults(data, debouncedSearch);
    const filtered = getFilteredResults(searched, activeFilters);
    setResults(filtered);
  }, [data, debouncedSearch, activeFilters]);

  useEffect(() => {
    if (data.length === 0) return;
    if (stableCacheKey) {
      setCache<TItem>(stableCacheKey, {
        data,
        pageNo: pageNoRef.current,
        completed,
      });
    }
  }, [data, completed, stableCacheKey]);

  // ─── Public API ───────────────────────────────────────────────────
  const refetch = () => {
    if (stableCacheKey) loadAllApiDataCache.delete(stableCacheKey);
    setRefreshNonce((n) => n + 1);
  };

  return {
    data: results, // searched + filtered view
    raw: data, // full unfiltered accumulation
    isLoading, // true while page 1 is in-flight
    isPageLoading, // true while pages 2+ are in-flight
    completed, // true once all pages have loaded (or errored)
    pageNo: pageNoRef.current,
    refetch,
  };
};
